import { useMediaQuery } from '@mantine/hooks'
import { Typography } from '../common/typography'
import { useLocation, useNavigate, useParams } from 'react-router'
import { IconChevronLeft } from '@tabler/icons-react'
import classes from './QuizResult.module.css'
import Header from '../common/components/header/Header'
import Meta from '../common/Meta'
import clsx from 'clsx'
import { AspectRatio, Button, Image, Loader } from '@mantine/core'
import {
  useCourseDetails,
  useAttemptResult,
  type FinishAttemptResponse,
} from './api'
import QuizFail from '../assets/quiz-fail.png'
import QuizSuccessful from '../assets/quiz-success.png'

interface LocationState {
  result?: FinishAttemptResponse
  attemptId?: number
}

export default function QuizResult() {
  const navigate = useNavigate()
  const location = useLocation()
  const { courseId } = useParams<{ courseId: string }>()
  const isDesktop = useMediaQuery('(min-width: 800px)')

  const state = location.state as LocationState | undefined
  const stateResult = state?.result
  const attemptId = state?.attemptId

  const { data: attemptData, isLoading } = useAttemptResult(attemptId)

  const { data: courseDetails } = useCourseDetails({
    course_id: Number(courseId),
  })

  if (!attemptId && !stateResult) {
    navigate(`/learning/course/${courseId}`, { replace: true })
    return null
  }

  const isAttemptInProgress = attemptData && !attemptData.completed_at
  if ((isLoading && !stateResult) || isAttemptInProgress) {
    return (
      <div className={classes.container}>
        <div className={classes.headerContainer}>
          {isDesktop && (
            <IconChevronLeft
              onClick={() => navigate('/learning')}
              className={classes.backIcon}
            />
          )}
          <Header title="Quiz Results" back="/learning" />
        </div>
        <Meta title="Quiz Results" />
        <div
          className={clsx(classes.resultWrapper, {
            [classes.resultWrapperMobile]: !isDesktop,
          })}
        >
          <div
            className={clsx(classes.resultContainer, {
              [classes.resultContainerMobile]: !isDesktop,
            })}
          >
            <Loader size="lg" />
            <div className={classes.wrapper}>
              <Typography fs="lg" fw={500} lh="150">
                Finalizing your results...
              </Typography>
            </div>
          </div>
        </div>
      </div>
    )
  }

  const result = attemptData || stateResult
  if (!result) {
    navigate(`/learning/course/${courseId}`, { replace: true })
    return null
  }

  const { score, percent, passed, learning_xp } = result

  const isFailed = courseDetails.status === 'failed'
  const hasUnlimitedAttempts = courseDetails.quiz_retry_attempts === 0
  const canRetry =
    hasUnlimitedAttempts ||
    courseDetails.attempts_count < Number(courseDetails.quiz_retry_attempts)
  const showTryAgain = !passed && isFailed && canRetry

  const scoreNum = parseFloat(score)
  const percentNum = parseFloat(percent)

  const handleTryAgain = () => {
    navigate(`/learning/course/${courseId}/consent`)
  }

  return (
    <div className={classes.container}>
      <div className={classes.headerContainer}>
        {isDesktop && (
          <IconChevronLeft
            onClick={() => navigate('/learning')}
            className={classes.backIcon}
          />
        )}
        <Header title="Quiz Results" back="/learning" />
      </div>
      <Meta title="Quiz Results" />
      <div
        className={clsx(classes.resultWrapper, {
          [classes.resultWrapperMobile]: !isDesktop,
        })}
      >
        <div
          className={clsx(classes.resultContainer, {
            [classes.resultContainerMobile]: !isDesktop,
          })}
        >
          <AspectRatio className={classes.resultIcon}>
            <Image src={passed ? QuizSuccessful : QuizFail} fit="scale-down" />
          </AspectRatio>

          <div className={classes.wrapper}>
            <Typography
              fs="xxl"
              fw={700}
              fc={{
                light: passed ? 'teal-7' : 'red-7',
                dark: passed ? 'teal-7' : 'red-7',
              }}
              lh="100"
            >
              {passed ? 'Congratulations!' : 'Not there yet!'}
            </Typography>
            {passed && (
              <Typography fw={700} lh="100">
                You earned {learning_xp} XP
              </Typography>
            )}
            <Typography
              fw={500}
              lh="100"
              fc={{ light: 'gray-7', dark: 'gray-5' }}
            >
              Score: {scoreNum.toFixed(1)} ({percentNum.toFixed(1)}%)
            </Typography>
          </div>

          <div className={classes.wrapper}>
            <Button
              className={classes.button}
              onClick={() => navigate('/learning')}
            >
              <Typography
                fs="sm"
                fw={500}
                fc={{ light: 'white', dark: 'white' }}
                tt="uppercase"
                lh="115"
              >
                Finish
              </Typography>
            </Button>
            {showTryAgain && (
              <Button
                className={classes.outlineButton}
                onClick={handleTryAgain}
              >
                <Typography
                  fs="sm"
                  fw={500}
                  fc={{ light: 'primary-5', dark: 'primary-3' }}
                  tt="uppercase"
                  lh="115"
                >
                  Try again
                </Typography>
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}
